import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { selectSelectedChannel, selectSelectedWorkSpace } from '../../../../store/app.selectors';
import {
  setHideAttachments,
  setHideEditPhone,
  setHideInfoButton,
  setHideInputChat,
  setLoadingChannel,
  setShowGoToOrderButton,
} from '../../../shared/store/ui.actions';
import { AuthenticationService } from '../../../user/services/authentication.service';
import { IChannelByParams } from '../../interfaces/IChannel';
import { IDealerSetting } from '../../interfaces/IDealerSettings';
import { IWorkspace, WorkspaceFeatures, WorkspaceShorter } from '../../interfaces/IWorkspace';
import { ChannelsService } from '../../services/channels.service';
import { PermissionsService } from '../../services/permissions.service';
import { SmartRepliesService } from '../../services/smart-replies.service';
import { MessagingTranslationService } from '../../services/translations.service';
import { WorkspacesService } from '../../services/workspaces.service';
import {
  getChannel,
  setAuthorizationTokenKey,
  setChannelInfo,
  setInputPhoneNumber,
  toggleSidebar,
  unsetChannel,
} from '../../store/chat.actions';
import { AppStateWithChat } from '../../store/chat.reducer';

@Component({
  selector: 'ngx-messaging-channel',
  templateUrl: './ngx-messaging-channel.component.html',
  styleUrls: ['./ngx-messaging-channel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MessagingChannelComponent implements OnInit, OnChanges {
  @Input() channelUID: string | null = null;
  @Input() channelByParams: IChannelByParams | null = null;
  @Input() workspace: WorkspaceShorter;
  @Input() dealerSettings: IDealerSetting[] = [];
  @Input() canUseCache = false;
  @Input() displayName: string;
  @Input() messagesStartDate: Date;
  @Input() readOnly: boolean;
  @Input() hideAttachments?: boolean;
  @Input() hideEditPhone?: boolean;
  @Input() subAccountUID: string;
  @Input() accountUID: string;
  @Input() accountPhone: string;
  @Input() hideInfoButton: boolean;
  @Input() view: string;
  @Input() tokenAuthorization: string;
  @Output() linkClicked = new EventEmitter<boolean>();
  @Output() orderIdEvent = new EventEmitter<string>();

  store = inject(Store<AppStateWithChat>);
  permissionService = inject(PermissionsService);
  workspaceService = inject(WorkspacesService);
  authService = inject(AuthenticationService);
  translationService = inject(MessagingTranslationService);
  smartRepliesService = inject(SmartRepliesService);
  channelsService = inject(ChannelsService);

  selectedChannel = toSignal(this.store.select(selectSelectedChannel), { initialValue: null });
  selectedWorkSpace = toSignal(this.store.select(selectSelectedWorkSpace), { initialValue: null as IWorkspace | null });

  async ngOnInit(): Promise<void> {
    if (this.tokenAuthorization) {
      this.store.dispatch(setAuthorizationTokenKey({ key: this.tokenAuthorization }));
    }
    this.store.dispatch(setHideInfoButton({ value: this.hideInfoButton }));
    this.store.dispatch(setLoadingChannel({ value: true }));
    if (this.workspace) {
      this.workspaceService.selectWorkspace(this.workspace);
    }
    await this.authService.loadProfile({
      dealerSettings: this.dealerSettings || [],
      subAccountUID: this.subAccountUID,
    });

    if (this.accountUID && this.accountPhone && this.subAccountUID) {
      await this.workspaceService.findAndSetWorkspace({
        accountPhone: this.accountPhone,
        accountUID: this.accountUID,
        subAccountUID: this.subAccountUID,
      });
    }
    this.store.dispatch(unsetChannel());
    this.store.dispatch(toggleSidebar({ active: false }));
    this.store.dispatch(setShowGoToOrderButton({ value: false }));
    this.loadChannel();
    if (this.readOnly) {
      this.store.dispatch(setHideInputChat({ value: true }));
    }
    if (this.hideAttachments) {
      this.store.dispatch(setHideAttachments({ value: true }));
    }
    if (this.hideEditPhone) {
      this.store.dispatch(setHideEditPhone({ value: true }));
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['displayName']?.firstChange && this.selectedChannel()?.displayName !== this.displayName) {
      this.updateChannelDisplayName();
    }
  }

  handleLinkClick(data: boolean): void {
    this.linkClicked.emit(data);
  }

  emitOrderIdEvent(event: string): void {
    this.orderIdEvent.emit(event);
  }

  loadChannel(): void {
    const selectedWorkSpace = this.selectedWorkSpace();
    if ((this.channelUID || this.channelByParams) && selectedWorkSpace) {
      if (this.channelByParams?.phoneNumber) {
        this.store.dispatch(setInputPhoneNumber({ phoneNumber: this.channelByParams.phoneNumber }));
      }
      const channelByParams = this.channelByParams
        ? {
            ...this.channelByParams,
            startDateTime: this.messagesStartDate ?? this.channelByParams.startDateTime,
          }
        : undefined;
      this.store.dispatch(
        getChannel({
          channelUID: this.channelUID,
          channelByParams,
          autoSelect: true,
          tryCreateChannel: true,
          updateChannel: this.channelByParams?.updateInfo,
        })
      );
    } else {
      this.store.dispatch(setLoadingChannel({ value: false }));
    }
  }

  updateChannelDisplayName(): void {
    const channel = this.selectedChannel();
    if (
      channel &&
      this.permissionService.checkWorkspaceFeatures([WorkspaceFeatures.canEditDisplayNameChannel]) &&
      this.displayName &&
      channel.displayName !== this.displayName
    ) {
      this.store.dispatch(
        setChannelInfo({
          channel,
          displayName: this.displayName,
        })
      );
    }
  }
}
